/**
 * Generate a full Tailwind-compatible color palette (50–900) from a single hex color.
 * Overrides --color-primary-* CSS variables so all existing Tailwind classes respond.
 */

/** Normalize "#RGB" → "#RRGGBB"; return null for anything that isn't a 3- or 6-digit hex. */
function normalizeHex(input: string | undefined | null): string | null {
  if (!input) return null;
  const m = input.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  const hex = m[1];
  if (hex.length === 3) {
    return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`.toLowerCase();
  }
  return `#${hex.toLowerCase()}`;
}

function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// Lightness targets for each shade — tuned to match typical Tailwind palettes
const SHADE_LIGHTNESS: Record<string, number> = {
  '50': 96,
  '100': 90,
  '200': 82,
  '300': 70,
  '400': 56,
  '500': 46,
  '600': 38,
  '700': 30,
  '800': 23,
  '900': 14,
};

export function generatePalette(hex: string): Record<string, string> | null {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;
  const [h, s] = hexToHsl(normalized);
  const palette: Record<string, string> = {};
  for (const [shade, lightness] of Object.entries(SHADE_LIGHTNESS)) {
    // Desaturate lighter shades slightly for a more natural feel
    const satAdj = lightness > 80 ? s * 0.7 : lightness > 60 ? s * 0.85 : s;
    palette[shade] = hslToHex(h, satAdj, lightness);
  }
  return palette;
}

/**
 * Generate branding CSS with configurable scope.
 * When scope is provided (e.g. '#login-preview'), CSS variables are scoped to
 * that selector instead of :root/.dark, allowing isolated preview rendering.
 * Uses '.lp-dark' instead of '.dark' for scoped dark mode to avoid triggering
 * Tailwind's app-wide dark variant.
 */
export function generateBrandingCSS(
  lightPrimary?: string,
  lightSecondary?: string,
  darkPrimary?: string,
  darkSecondary?: string,
  scope?: string,
): string {
  // Normalize and drop anything that isn't a valid hex — prevents writing
  // `#NaNNaNNaN` into CSS (which makes every bg-primary-* class transparent).
  const lp = normalizeHex(lightPrimary);
  const ls = normalizeHex(lightSecondary);
  const dp = normalizeHex(darkPrimary) || lp;
  const ds = normalizeHex(darkSecondary) || ls;

  const lightSelector = scope || ':root';
  const darkSelector = scope ? `${scope}.lp-dark` : '.dark';

  let css = '';

  if (lp || ls) {
    css += `${lightSelector} {\n`;
    if (lp) {
      const palette = generatePalette(lp);
      if (palette) {
        for (const [shade, hex] of Object.entries(palette)) {
          css += `  --color-primary-${shade}: ${hex};\n`;
        }
        css += `  --shadow-dark-glow: 0 0 20px ${lp}26;\n`;
      }
    }
    if (ls) {
      css += `  --color-berry: ${ls};\n`;
    }
    css += '}\n';
  }

  if (dp || ds) {
    css += `${darkSelector} {\n`;
    if (dp) {
      const palette = generatePalette(dp);
      if (palette) {
        for (const [shade, hex] of Object.entries(palette)) {
          css += `  --color-primary-${shade}: ${hex};\n`;
        }
        css += `  --shadow-dark-glow: 0 0 20px ${dp}26;\n`;
      }
    }
    if (ds) {
      css += `  --color-berry: ${ds};\n`;
    }
    css += '}\n';
  }

  return css;
}

/**
 * Apply tenant branding colors as a <style> tag with separate :root (light) and
 * .dark selector rules so Tailwind's dark: variants pick up the correct palette.
 *
 * Dark mode colors fall back to light mode colors if not provided.
 */
export function applyTenantBranding(
  lightPrimary?: string,
  lightSecondary?: string,
  darkPrimary?: string,
  darkSecondary?: string,
) {
  clearTenantBranding();

  const css = generateBrandingCSS(lightPrimary, lightSecondary, darkPrimary, darkSecondary);
  if (!css) return;

  const style = document.createElement('style');
  style.id = 'tenant-branding';
  style.textContent = css;
  document.head.appendChild(style);
}

/**
 * Remove tenant overrides — removes injected <style> tag and any legacy inline styles.
 */
export function clearTenantBranding() {
  // Remove injected style tag
  document.getElementById('tenant-branding')?.remove();

  // Clear any legacy inline styles set by older applyTenantBranding
  const root = document.documentElement;
  for (const shade of Object.keys(SHADE_LIGHTNESS)) {
    root.style.removeProperty(`--color-primary-${shade}`);
  }
  root.style.removeProperty('--shadow-dark-glow');
  root.style.removeProperty('--color-berry');
}
